"use client";

import { createTrade, getPackages, getPriceV2, getTradeStatus } from "@/04-drivers/dal/payment";
import { Modal, ModalBody, ModalContent, ModalHeader, useDisclosure } from "@nextui-org/react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "../components/ui/button";
import { Fragment, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { useToast } from "../components/ui/use-toast";
import { GenericError } from "@/04-drivers/dal/error";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Image from "next/image";
import "./Payment.css";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { LoadingOverlay } from "@mantine/core";
import { useUserInfo } from "@/app/[locale]/hooks";
import Exchange from "./Exchange";
import { toast as sonnerToast } from "sonner";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { basicFeatures, enterpriseFeatures, exclusiveFeatures, experienceFeatures, professionalFeatures } from "@/app/[locale]/tiktok/pricing/const";
import { useRequest } from "ahooks";
import { RoleType } from "@/00-shared/const";
import { seaBayModalStore } from "./SeaBayPayment";
import { paymentModalStore } from "@/app/[locale]/tiktok/payment-modal";
import { Avatar, AvatarImage } from "@/03-presenters/components/ui/avatar";
import { hashEmailToRandomDigit } from "@/03-presenters/shared/helper";
import { getMemberByRoleId } from "@/app/[locale]/tiktok/account/UserInfoCard";
import { cx } from "class-variance-authority";
type Version = "enterprise" | "exclusive" | "professional" | "basic" | "experience";
export const activityVersion40off = ["experience", "basic"];
export const activityVersion30off = [""];
export const activityVersion = [...activityVersion40off, ...activityVersion30off];
const couponCodeCheck = async (couponCode: string) => {
  const res = await fetch("/api/v3/account/private_code/check", {
    method: "POST",
    body: JSON.stringify({
      code: couponCode
    }),
    headers: {
      "Content-Type": "application/json"
    }
  });
  const data = await res.json();
  if (data?.base_resp?.code !== 0) {
    throw new Error(data?.base_resp?.msg);
  }
  return data;
};
const useCouponCodeCheck = () => {
  const {
    runAsync,
    loading
  } = useRequest((couponCode: string) => couponCodeCheck(couponCode), {
    manual: true,
    onError: error => {
      sonnerToast.error("折扣码无效", {
        richColors: true,
        position: "top-center"
      });
    }
  });
  return {
    runAsync,
    loading
  };
};
const InterestsItems = {
  enterprise: enterpriseFeatures.features.filter(f => f.value !== false).map(f => f.description + (f.value !== true ? f.value : "")),
  exclusive: exclusiveFeatures.features.filter(f => f.value !== false).map(f => f.description + (f.value !== true ? f.value : "")),
  professional: professionalFeatures.features.filter(f => f.value !== false).map(f => f.description + (f.value !== true ? f.value : "")),
  basic: basicFeatures.features.filter(f => f.value !== false).map(f => f.description + (f.value !== true ? f.value : "")),
  experience: experienceFeatures.features.filter(f => f.value !== false).map(f => f.description + (f.value !== true ? f.value : ""))
};
export interface Detail {
  discount: number;
  discount_amount: number;
  discout_for_sub_account: number;
  discount_amount_for_sub_account: number;
  desc: string;
  total_discount: number;
  total_discount_amount: number;
}
export interface PackageType {
  package_id: number;
  platform_id: number;
  price_info: PriceInfo;
  desc: string;
  package_type: number;
  package_time_type: number;
  last_time: number;
  ctime: number;
  mtime: number;
}
export interface PriceInfo {
  original_total_amount: number;
  actual_total_amount: number;
  actual_monthly_amount: number;
  monthly_amount: number;
  monthly_amount_per_account: number;
  discount_percentage_compare_to_monthly: number;
  discount_amount_compare_to_monthly: number;
  currency: string;
  sub_account_count: number;
  refund_amount: number;
  detail_list: Array<Detail>;
  original_monthly_amount: number;
  actual_monthly_amount_per_account: number;
}
export default function PaymentV2({
  hideToast,
  defaultVersion = "enterprise",
  type = "UNAUTHORIZED_ERROR",
  showSeaBayTips = false,
  defaultCouponType,
  onClose
}: {
  hideToast?: boolean;
  defaultVersion?: string;
  type?: "UNAUTHORIZED_ERROR" | "OUT_OF_LIMIT_ERROR" | "SEA_BAY_INSUFFICIENT";
  showSeaBayTips?: boolean;
  defaultCouponType?: string;
  onClose: () => void;
}) {
  // 1: 微信支付 2: 支付宝支付
  const [paymentMethod, setPaymentMethod] = useState<number>(1);
  const [alertDialogIsOpen, setAlertDialogIsOpen] = useState(false);
  const [version, setVersion] = useState(defaultVersion);
  const setSeaBayModalIsOpen = seaBayModalStore(state => state.setIsOpen);
  const payModalOnOpenChange = paymentModalStore(state => state.setIsOpen);
  const {
    accountInfo,
    userAuthInfo: authInfo,
    isLogin,
    user,
    isNeverPaid,
    isReferred,
    isHighLevelUser,
    userCouponList,
    isOwner,
    isTheMostHighLevelUser
  } = useUserInfo();
  const {
    runAsync,
    loading
  } = useCouponCodeCheck();
  // 优惠折扣类型
  const [discountType, setDiscountType] = useState<string | undefined>(defaultCouponType ? "1" : undefined);
  // 优惠券类型
  const [couponType, setCouponType] = useState<string | undefined>(defaultCouponType);
  // 折扣券码
  const [couponCode, setCouponCode] = useState<string | undefined>();
  const [isValidating, setIsValidating] = useState(false);

  // 选择的支付类型， 0 季付，1 年付
  const [selectedPayType, setSelectedPayType] = useState<0 | 1>(0);
  const currentRole = authInfo?.role_list?.[0];
  const currentRoleId = currentRole?.id;
  // 只有普通用户展示兑换会员
  const isCommonUser = currentRoleId === RoleType.NORMAL_V2;
  const showExchange = isCommonUser && !isReferred;
  const {
    toast
  } = useToast();
  const {
    isOpen: wechatPayIsOpen,
    onOpen: wechatPayOnOpen,
    onClose: wechatPayOnClose,
    onOpenChange: wechatPayOnOpenChange
  } = useDisclosure();
  const {
    data: packages,
    isLoading: packagesIsLoading
  } = useSWR("package", async () => {
    const res = await getPackages("user.personal.payment.package");
    const paymentPackageList = res?.payment_package_list || [];
    if (res?.base_resp?.code !== 0) {
      throw new Error(res.msg);
    }
    const p1 = paymentPackageList.filter((p: any) => p?.package_type === RoleType.ENTERPRISE_V3).sort((a: any, b: any) => b?.price_info?.actual_total_amount - a?.price_info?.actual_total_amount) as Array<PackageType>;
    const p2 = paymentPackageList.filter((p: any) => p?.package_type === RoleType.EXCLUSIVE_V3).sort((a: any, b: any) => b?.price_info?.actual_total_amount - a?.price_info?.actual_total_amount) as Array<PackageType>;
    const p3 = paymentPackageList.filter((p: any) => p?.package_type === RoleType.PROFESSIONAL_V3).sort((a: any, b: any) => b?.price_info?.actual_total_amount - a?.price_info?.actual_total_amount) as Array<PackageType>;
    const p4 = paymentPackageList.filter((p: any) => p?.package_type === RoleType.BASIC_V3).sort((a: any, b: any) => b?.price_info?.actual_total_amount - a?.price_info?.actual_total_amount) as Array<PackageType>;
    const p5 = paymentPackageList.filter((p: any) => p?.package_type === RoleType.EXPERIENCE_V3).sort((a: any, b: any) => b?.price_info?.actual_total_amount - a?.price_info?.actual_total_amount) as Array<PackageType>;
    return [{
      originalVal: p5,
      packageType: RoleType.EXPERIENCE_V3,
      customDesc: "入门版",
      subDesc: experienceFeatures.subTitle,
      customIcon: "/pricing/experience.webp",
      customVal: "experience"
    }, {
      originalVal: p4,
      packageType: RoleType.BASIC_V3,
      customDesc: "标准版",
      subDesc: basicFeatures.subTitle,
      customIcon: "/pricing/basic.webp",
      customVal: "basic"
    }, {
      originalVal: p3,
      packageType: RoleType.PROFESSIONAL_V3,
      customDesc: "专业版",
      subDesc: professionalFeatures.subTitle,
      customIcon: "/pricing/professional.webp",
      customVal: "professional"
    }, {
      originalVal: p2,
      packageType: RoleType.EXCLUSIVE_V3,
      customDesc: "尊享版",
      subDesc: exclusiveFeatures.subTitle,
      customIcon: "/pricing/exclusive.webp",
      customVal: "exclusive"
    }, {
      originalVal: p1,
      packageType: RoleType.ENTERPRISE_V3,
      customDesc: "企业版",
      subDesc: enterpriseFeatures.subTitle,
      customIcon: "/pricing/enterprise.webp",
      customVal: "enterprise"
    }];
  });
  const allPackage = packages?.find(p => p.customVal === version)?.originalVal;
  const selectedPackage = allPackage?.[selectedPayType];
  const couponList = userCouponList.filter(c => c.package_id_list.includes(selectedPackage?.package_id!)).map(item => ({
    value: item.code,
    label: item.desc
  }));
  const discountTypeList = [{
    label: "优惠卷",
    value: "1",
    disabled: couponList.length === 0 || activityVersion.includes(version)
  }, {
    label: "折扣码",
    value: "2",
    disabled: false
  }];
  const disableSelect = discountTypeList.every(d => d.disabled);
  const selectedCouponLabel = useMemo(() => {
    return couponList.find(c => c.value === couponType)?.label || "";
  }, [couponList, couponType]);
  const {
    data: priceData,
    isLoading: priceDataIsLoading
  } = useSWR(packages && version && accountInfo?.account_id !== undefined ? ["/price", version, accountInfo?.account_id, couponType, isValidating, discountType, isValidating ? couponCode : undefined] : null, async () => {
    const queryPackages = packages?.find(p => p.customVal === version)?.originalVal;
    if (!queryPackages) throw new Error("package not found");
    const reqs = queryPackages.map(async p => {
      const packageTypeList = [6, 7, 8, 9, 10];
      const selectedDescIdx = packageTypeList.findIndex(d => d === p.package_type);
      const currentDescIdx = packageTypeList.findIndex(d => d === authInfo?.role_list?.[0]?.id);
      const isUpgrade = selectedDescIdx < currentDescIdx;
      const res = await getPriceV2({
        account_id: accountInfo?.account_id!,
        package_id: p.package_id,
        sub_account_count: 0,
        resourceKey: "user.personal.payment.price",
        isUpgrade: false,
        couponType: discountType === "1" ? couponType : undefined,
        discountCode: discountType === "2" && couponCode && isValidating ? couponCode : undefined
      });
      return {
        ...(res?.price_info as PriceInfo),
        package_id: p.package_id
      };
    });
    const res = await Promise.all(reqs);
    return res;
  }, {
    revalidateOnFocus: false,
    keepPreviousData: true,
    revalidateIfStale: false
  });
  const currentPrice = priceData?.find(p => p.package_id === selectedPackage?.package_id);
  const createTradeReq = async (url: string, {
    arg
  }: {
    arg: {
      package_id: number;
      sub_accout_count: number | undefined;
      isUpgrade: boolean;
      couponType?: string;
      discountCode?: string;
    };
  }) => {
    try {
      const res = await createTrade(accountInfo?.account_id!, arg.package_id, paymentMethod, Math.max(arg.sub_accout_count ?? 0, 0), "user.personal.payment.trade", arg.isUpgrade, arg.couponType, arg.discountCode);
      return res;
    } catch (error) {
      if (!(error instanceof GenericError)) {
        throw error;
      }
      if (error.name === "HAVING_UNFINISHED_ORDER_ERROR") {
        setAlertDialogIsOpen(true);
        throw error;
      }
      if (error.name === "LOWER_PACKAGE_ERROR") {
        sonnerToast.warning(`您当前是${currentRole?.name}会员，不可购买更低版本套餐`, {
          position: "top-center",
          richColors: true
        });
      }
      if (error.name === "INVALID_POLICY_ERROR") {
        sonnerToast.warning(error.message, {
          position: "top-center",
          richColors: true
        });
      }
    }
  };
  const {
    trigger,
    data: tradeInfo,
    isMutating
  } = useSWRMutation("/createTrade", createTradeReq, {
    onError: (error: GenericError) => {
      if (!(error instanceof GenericError)) {
        toast({
          variant: "destructive",
          title: "未知错误",
          description: "请联系管理员"
        });
      }
      if (error.name === "HAVING_UNFINISHED_ORDER_ERROR") {
        return;
      }
      toast({
        variant: "destructive",
        title: "未知错误",
        description: error.message
      });
    }
  });
  const {
    data: tradeStatus,
    isLoading: tradeStatusIsLoading
  } = useSWR(() => isLogin && selectedPackage?.package_id && tradeInfo?.order_id ? "payment" + tradeInfo?.order_id : null, async () => {
    const res = await getTradeStatus(accountInfo?.account_id!, selectedPackage?.package_id!, paymentMethod, tradeInfo?.order_id, "user.personal.payment.payment");
    return res;
  }, {
    refreshInterval: 1500,
    onSuccess: data => {
      if (data?.order?.trade_status === "SUCCESS") {
        window.location.reload();
      }
    }
  });
  // 6000 元以上隐藏微信支付
  const isHideWechatPay = currentPrice?.actual_total_amount && Number((currentPrice.actual_total_amount / 100).toFixed(0)) > 6000;
  useEffect(() => {
    if (isHideWechatPay) {
      setPaymentMethod(2);
    }
  }, [isHideWechatPay]);
  const avatarImg = `/avatar/${hashEmailToRandomDigit(user?.id as string ?? "")}.png`;
  const roleId = authInfo?.role_list?.[0]?.id;
  const member = getMemberByRoleId(roleId ?? 0);
  if (packagesIsLoading) return <div></div>;
  const getTip = () => {
    switch (type) {
      case "OUT_OF_LIMIT_ERROR":
        return isTheMostHighLevelUser ? "已超出用量，请扫码联系客服" : "已超出用量，请升级会员套餐";
      case "SEA_BAY_INSUFFICIENT":
        return "暂不支持使用该功能，请升级版本或扫码咨询导师";
      default:
        return "无此功能权限，请升级会员套餐";
    }
  };
  return <Fragment>
      <div className="flex h-20 w-full items-center justify-between bg-[#1B1735] pl-9 pr-14">
        <div className="flex items-center gap-2">
          <Avatar className="size-[57px]" data-sentry-element="Avatar" data-sentry-source-file="PaymentV2.tsx">
            <AvatarImage src={avatarImg} alt="avatar" data-sentry-element="AvatarImage" data-sentry-source-file="PaymentV2.tsx" />
          </Avatar>
          <div className="flex flex-col gap-2">
            <span className="text-white">{user?.username}</span>
            <span className="text-sm text-white/50">用户ID: {user?.id}</span>
          </div>
        </div>
        {member?.title && <div className="flex items-center justify-center gap-2 rounded-sm bg-white/10 p-3 text-xs">
            <span className="text-white/40">当前会员等级</span>
            <span className="text-white">{member?.title}</span>
          </div>}

        <div className="absolute right-4 top-4 cursor-pointer" onClick={onClose}>
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="PaymentV2.tsx">
            <path d="M1 9.48535L9.48528 1.00007" stroke="#A4A2AE" strokeWidth="1.5" strokeLinecap="round" data-sentry-element="path" data-sentry-source-file="PaymentV2.tsx" />
            <path d="M1 1L9.48528 9.48528" stroke="#A4A2AE" strokeWidth="1.5" strokeLinecap="round" data-sentry-element="path" data-sentry-source-file="PaymentV2.tsx" />
          </svg>
        </div>
      </div>
      <div>
        {!hideToast && <div className="fixed left-[50%] top-[5%] z-20 mx-auto mb-4 w-fit -translate-x-1/2 rounded-lg bg-white px-4 py-1">
            <span>
              您当前为「
              <span className="text-[#7A5CF2]">
                {authInfo?.role_list?.[0]?.name}
              </span>
              」，
              {getTip()}
            </span>
          </div>}
        {isHighLevelUser && showSeaBayTips && isOwner && <div className="fixed left-[50%] top-[10%] z-20 mx-auto mb-4 w-fit -translate-x-1/2 rounded-lg bg-white px-4 py-1">
            <span>AI工具箱额度不足？</span>
            <span className="cursor-pointer text-[#7A5CF2]" onClick={() => {
          setSeaBayModalIsOpen(true);
          payModalOnOpenChange(false);
        }}>
              购买加量包
            </span>
            <span>继续使用</span>
          </div>}
        <Tabs onValueChange={value => {
        if (value === "exchange") {
          return;
        }
        setCouponType(undefined);
        setDiscountType(undefined);
        setCouponCode(undefined);
        setIsValidating(false);
        setVersion(value);
        setSelectedPayType(0);
      }} className="rounded-lg" defaultValue={defaultVersion} data-sentry-element="Tabs" data-sentry-source-file="PaymentV2.tsx">
          <TabsList className="items-cente h-[90px] w-full bg-[#E6E5E8]" data-sentry-element="TabsList" data-sentry-source-file="PaymentV2.tsx">
            {packages?.map(p => {
            return <TabsTrigger key={p.customVal} className="flex h-full flex-1 flex-col items-center justify-center gap-1 rounded-t-md text-lg data-[state=active]:text-xl data-[state=active]:font-medium data-[state=active]:text-black" value={p.customVal}>
                  <div className="flex items-center justify-center gap-2">
                    <Image src={p.customIcon} width={16} height={16} alt="icon" />
                    {p.customDesc}
                  </div>
                  <span className="min-h-8 max-w-32 text-wrap text-xs leading-4 text-[#818B96]">
                    {p.subDesc}
                  </span>
                </TabsTrigger>;
          })}
            {showExchange && <TabsTrigger value="exchange" className="tab-child flex flex-1 flex-col items-center justify-center">
                会员兑换
                <span className="text-xs text-[#818B96]">新用户专享</span>
              </TabsTrigger>}
          </TabsList>
          {packages?.map(p => {
          return <TabsContent key={p.customVal} className="mt-0 rounded-b-lg rounded-t-none p-0 data-[state=inactive]:!hidden" value={p.customVal}>
                <div className="grid grid-cols-[auto_250px] gap-2">
                  <div className="relative flex flex-col gap-4 bg-white pb-4">
                    <LoadingOverlay visible={priceDataIsLoading} zIndex={11} overlayProps={{
                  radius: "sm",
                  blur: 2
                }} />
                    <div className="flex w-full justify-between gap-9 p-6">
                      {allPackage?.map((packageItem, index) => {
                    const innerPrice = priceData?.find(p => p.package_id === packageItem.package_id);
                    const isSelected = packageItem.package_id === selectedPackage?.package_id;
                    return <div key={packageItem.package_id} className={cx("relative flex-1 cursor-pointer overflow-hidden p-0.5")} onClick={() => {
                      setSelectedPayType(index as 0 | 1);
                    }}>
                            <div className={cx("absolute inset-0 rounded-lg bg-gradient-to-r from-[#4f4cfe] to-[#da7ed0] opacity-0", isSelected && "opacity-100")} />

                            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        zIndex: 20,
                        opacity: isSelected ? 1 : 0
                      }}>
                              <path d="M17.6 26.4L44 0V36C44 40.4183 40.4183 44 36 44H0L17.6 26.4Z" fill="#DA7FDE" />
                              <path fillRule="evenodd" clipRule="evenodd" d="M32.7619 28.6838C33.0517 28.954 33.081 29.4232 32.8275 29.732L29.2577 33.9927C28.6858 34.689 27.6621 34.665 27.1196 33.9424L25.1528 31.5958C24.9123 31.2755 24.961 30.808 25.2617 30.5517C25.5623 30.2955 26.0011 30.3474 26.2416 30.6677L27.9977 33.0068L31.7782 28.7537C32.0317 28.4449 32.4721 28.4137 32.7619 28.6838Z" fill="white" />
                            </svg>
                            <svg width="71" height="26" viewBox="0 0 71 26" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        zIndex: 20,
                        opacity: allPackage?.length === 2 && index === 0 ? 1 : 0
                      }}>
                              <path d="M0 0H63C67.4183 0 71 3.58172 71 8V26H8C3.58172 26 0 22.4183 0 18V0Z" fill="#6E6BFF" fillOpacity="0.1" />
                              <path d="M16.48 10.46L17.71 10.78C17.53 11.2733 17.3167 11.7567 17.07 12.23C16.83 12.7033 16.5667 13.1433 16.28 13.55C16 13.95 15.71 14.3 15.41 14.6C15.33 14.5333 15.2267 14.45 15.1 14.35C14.9733 14.2433 14.8433 14.1433 14.71 14.05C14.5833 13.9567 14.47 13.88 14.37 13.82C14.6767 13.56 14.9633 13.2533 15.23 12.9C15.4967 12.54 15.7367 12.1533 15.95 11.74C16.1633 11.32 16.34 10.8933 16.48 10.46ZM16.7 11.6H23.06V12.76H16.12L16.7 11.6ZM15.99 13.97H22.82V15.09H17.19V17.19H15.99V13.97ZM14.4 16.6H23.6V17.75H14.4V16.6ZM18.93 12.2H20.17V19.9H18.93V12.2ZM27.51 12.67H33.68V13.88H27.51V12.67ZM31.33 10.62H32.57V18.44C32.57 18.8267 32.52 19.11 32.42 19.29C32.3267 19.4767 32.1567 19.6167 31.91 19.71C31.67 19.7967 31.3533 19.8533 30.96 19.88C30.5733 19.9067 30.1033 19.9167 29.55 19.91C29.5367 19.79 29.5033 19.66 29.45 19.52C29.4033 19.38 29.3467 19.2367 29.28 19.09C29.22 18.9433 29.1567 18.82 29.09 18.72C29.35 18.7267 29.61 18.7333 29.87 18.74C30.1367 18.7467 30.37 18.75 30.57 18.75C30.77 18.7433 30.91 18.74 30.99 18.74C31.1167 18.74 31.2033 18.7167 31.25 18.67C31.3033 18.6233 31.33 18.5467 31.33 18.44V10.62ZM27.96 15.09L29.02 14.6C29.1733 14.84 29.3367 15.1033 29.51 15.39C29.6833 15.67 29.85 15.9467 30.01 16.22C30.17 16.4867 30.2967 16.72 30.39 16.92L29.25 17.51C29.17 17.2967 29.0567 17.0533 28.91 16.78C28.7633 16.5067 28.6067 16.22 28.44 15.92C28.2733 15.62 28.1133 15.3433 27.96 15.09ZM25.67 13.44L26.87 12.24L26.89 12.25V19.88H25.67V13.44ZM26.66 10.56L27.85 10.93C27.6233 11.51 27.3567 12.09 27.05 12.67C26.7433 13.25 26.4133 13.7967 26.06 14.31C25.7067 14.8233 25.34 15.27 24.96 15.65C24.92 15.55 24.8567 15.42 24.77 15.26C24.69 15.1 24.6033 14.9433 24.51 14.79C24.4167 14.63 24.3333 14.5 24.26 14.4C24.58 14.0933 24.89 13.7367 25.19 13.33C25.49 12.9167 25.7667 12.4733 26.02 12C26.2733 11.5267 26.4867 11.0467 26.66 10.56ZM35.8253 19L42.0114 6.75568V6.65625H34.831V4.45455H44.7386V6.70597L38.5597 19H35.8253ZM54.2683 10.54L55.1683 11.5C54.7749 11.64 54.3416 11.7733 53.8683 11.9C53.3949 12.02 52.9116 12.1267 52.4183 12.22C51.9316 12.3067 51.4583 12.3833 50.9983 12.45C50.9716 12.31 50.9216 12.1467 50.8483 11.96C50.7749 11.7733 50.7016 11.62 50.6283 11.5C51.0683 11.42 51.5083 11.3333 51.9483 11.24C52.3949 11.14 52.8183 11.03 53.2183 10.91C53.6249 10.79 53.9749 10.6667 54.2683 10.54ZM50.1883 11.25L51.6583 11.43C51.6516 11.5233 51.5616 11.5867 51.3883 11.62V14.58C51.3883 14.9667 51.3683 15.39 51.3283 15.85C51.2949 16.31 51.2249 16.78 51.1183 17.26C51.0183 17.74 50.8616 18.2033 50.6483 18.65C50.4416 19.0967 50.1616 19.5 49.8083 19.86C49.7549 19.7667 49.6749 19.6633 49.5683 19.55C49.4683 19.4433 49.3583 19.34 49.2383 19.24C49.1249 19.14 49.0183 19.0633 48.9183 19.01C49.2049 18.6967 49.4316 18.3567 49.5983 17.99C49.7716 17.6233 49.8983 17.2467 49.9783 16.86C50.0649 16.4667 50.1216 16.0767 50.1483 15.69C50.1749 15.2967 50.1883 14.9233 50.1883 14.57V11.25ZM50.7983 13.87H55.4183V15.01H50.7983V13.87ZM52.8183 14.49H54.0083V19.89H52.8183V14.49ZM45.9383 15.61C46.2516 15.5433 46.6049 15.4633 46.9983 15.37C47.3983 15.2767 47.8149 15.1767 48.2483 15.07C48.6883 14.9567 49.1283 14.8433 49.5683 14.73L49.7083 15.83C49.1083 15.9967 48.5016 16.16 47.8883 16.32C47.2816 16.48 46.7283 16.6267 46.2283 16.76L45.9383 15.61ZM46.1083 12.39H49.5583V13.5H46.1083V12.39ZM47.3683 10.5H48.5483V18.6C48.5483 18.8933 48.5149 19.12 48.4483 19.28C48.3883 19.44 48.2716 19.57 48.0983 19.67C47.9316 19.7567 47.7249 19.8133 47.4783 19.84C47.2316 19.8667 46.9316 19.88 46.5783 19.88C46.5583 19.7267 46.5149 19.5433 46.4483 19.33C46.3883 19.1167 46.3216 18.9333 46.2483 18.78C46.4416 18.7867 46.6283 18.7933 46.8083 18.8C46.9883 18.8 47.1116 18.8 47.1783 18.8C47.2449 18.7933 47.2916 18.7767 47.3183 18.75C47.3516 18.7233 47.3683 18.6767 47.3683 18.61V10.5Z" fill="#4E4CFD" />
                            </svg>

                            <div className="relative z-10 flex h-[140px] flex-col items-center justify-center gap-2 rounded-lg border bg-white p-4">
                              <span className="text-xl">
                                {allPackage?.length === 1 ? "月付" : index === 0 ? "年付" : "季付"}
                              </span>
                              <span className="text-2xl font-medium text-[#3631FF]">
                                {priceFormatter(innerPrice?.actual_total_amount)}
                              </span>
                              <span className="text-[#818B96]">
                                {`每账号 ${priceFormatter(innerPrice?.actual_monthly_amount_per_account)}
                              元 / 月`}
                              </span>
                            </div>
                          </div>;
                  })}
                    </div>
                    <div className="flex flex-1 gap-6">
                      <div className="flex flex-1 flex-col gap-4 px-4">
                        <div className="flex justify-between gap-2">
                          <span className="pt-2 text-sm">优惠折扣</span>
                          <div className="flex flex-col gap-2 text-xs">
                            <Select value={discountType} onValueChange={setDiscountType} disabled={disableSelect}>
                              <SelectTrigger className="h-8 w-40">
                                <SelectValue placeholder="请选择优惠折扣">
                                  {discountType ? discountType === "1" ? "优惠卷" : "折扣码" : "请选择优惠折扣"}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {discountTypeList.map(d => {
                                return <SelectItem key={d.value} value={d.value} disabled={d.disabled}>
                                        {d.label}
                                      </SelectItem>;
                              })}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            {discountType === "1" && <Select value={couponType} onValueChange={setCouponType}>
                                <SelectTrigger className="h-8 w-32">
                                  <SelectValue placeholder="请选择优惠券">
                                    {selectedCouponLabel}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {couponList.map(d => {
                                return <SelectItem key={d.value} value={d.value}>
                                          {d.label}
                                        </SelectItem>;
                              })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>}
                            {discountType === "2" && <div className="flex items-center gap-2">
                                <Input value={couponCode} onChange={e => {
                            setIsValidating(false);
                            setCouponCode(e.target.value);
                          }} className="h-8 w-32" placeholder="请输入折扣码" />
                                <Button className="h-8 w-fit disabled:cursor-not-allowed disabled:opacity-50" disabled={loading} onClick={async () => {
                            if (!couponCode) return;
                            await runAsync(couponCode);
                            setIsValidating(true);
                          }}>
                                  验证
                                  {loading && <Loader2 className="ml-2 size-4 animate-spin" color="gray" />}
                                </Button>
                              </div>}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm">支付方式</span>
                          <ToggleGroup className="w-fit gap-2" type="single" value={paymentMethod.toString()} onValueChange={value => {
                        if (!value) {
                          return;
                        }
                        setPaymentMethod(Number(value));
                      }}>
                            {!isHideWechatPay && <ToggleGroupItem className="h-8 text-xs ring-1 data-[state=on]:bg-[#F2F3FF] data-[state=on]:ring-[#7A5CF2]" value="1">
                                <Image alt="wechat" width={16} height={16} src="/images/clients/wechat.svg" className="mr-2" />
                                <span>微信支付</span>
                              </ToggleGroupItem>}
                            <ToggleGroupItem className="h-8 text-xs ring-1 data-[state=on]:bg-[#F2F3FF] data-[state=on]:ring-[#7A5CF2]" value="2">
                              <Image alt="alipay" width={16} height={16} src="/images/clients/alipay.svg" className="mr-2" />
                              <span>支付宝支付</span>
                            </ToggleGroupItem>
                          </ToggleGroup>
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className="text-sm">订单结算</span>
                          <div className="flex flex-col gap-2 text-xs text-[#8789A0]">
                            <div className="flex items-center justify-between">
                              <span>原价：</span>
                              <span className="text-xs">
                                {currentPrice?.original_total_amount ? priceFormatter(currentPrice.original_total_amount) : ""}
                              </span>
                            </div>
                            {currentPrice?.refund_amount ? <div className="flex items-center justify-between text-[#6F58F4]">
                                <span>会员升级抵扣：</span>
                                <span className="text-xs">
                                  {currentPrice?.refund_amount ? "-" + priceFormatter(currentPrice.refund_amount) : ""}
                                </span>
                              </div> : null}
                            {currentPrice?.detail_list.map(detail => {
                          return <div key={detail.desc} className="flex items-center justify-between">
                                  <span>{detail.desc}：</span>
                                  <span className="text-xs text-[#DB0101]">
                                    -
                                    {priceFormatter(detail.total_discount_amount)}
                                  </span>
                                </div>;
                        })}
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-[#8789A0]">
                              实付金额：
                            </span>
                            <span className="text-xl text-[#3631FF]">
                              {currentPrice?.actual_total_amount ? priceFormatter(currentPrice.actual_total_amount) : ""}
                            </span>
                          </div>
                        </div>
                        <Button disabled={packagesIsLoading || priceDataIsLoading || isMutating} className="gradient-btn px-6" onClick={async () => {
                      if (!selectedPackage?.package_id) return;
                      if ([1, 2, 3, 4, 5].includes(authInfo?.role_list?.[0]?.id || 12)) {
                        sonnerToast.warning("由于系统升级，您当前版本不支持网页购买，如需续费/升级，请扫码联系客服", {
                          richColors: true,
                          position: "top-center"
                        });
                        return;
                      }
                      const packageTypeList = [6, 7, 8, 9, 10];
                      const selectedDescIdx = packageTypeList.findIndex(d => d === selectedPackage.package_type);
                      const currentDescIdx = packageTypeList.findIndex(d => d === authInfo?.role_list?.[0]?.id);
                      const isUpgrade = selectedDescIdx < currentDescIdx;
                      const data = await trigger({
                        package_id: selectedPackage.package_id,
                        sub_accout_count: 0,
                        isUpgrade,
                        couponType: discountType === "1" && couponType ? couponType : undefined,
                        discountCode: discountType === "2" && isValidating ? couponCode : undefined
                      });
                      data && wechatPayOnOpen();
                    }}>
                          {packagesIsLoading || priceDataIsLoading || isMutating && <Loader2 className="mr-2 size-4 animate-spin" />}
                          立即支付
                        </Button>
                      </div>
                      <div className="flex flex-1 flex-col gap-4">
                        <QrCode />
                        <div className="flex flex-row items-center justify-between">
                          <div className="flex flex-col gap-2 text-xs text-[#8789A0]">
                            <div className="flex">
                              <span>付款即表同意 </span>
                              <Link className="text-primary" href="/serviceAgreement" target="_blank">
                                购买协议
                              </Link>
                            </div>
                            <span>
                              *付款成功后权限立即生效如需发票请前往
                              <Link href="/zh/tiktok/account" className="text-[#6F58F4]">
                                账户中心-开具发票
                              </Link>
                              开票
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full rounded-md bg-white px-3">
                    <div className="flex size-full flex-col gap-4 bg-[#F8F8FB] p-4">
                      {InterestsItems[p.customVal as Version].map(item => {
                    return <InterestsItem key={item}>{item}</InterestsItem>;
                  })}
                    </div>
                  </div>
                </div>
              </TabsContent>;
        })}
          <TabsContent value="exchange" className="mt-0 rounded-lg bg-[#F7F7F8] p-4 data-[state=inactive]:!hidden" data-sentry-element="TabsContent" data-sentry-source-file="PaymentV2.tsx">
            <Exchange data-sentry-element="Exchange" data-sentry-source-file="PaymentV2.tsx" />
          </TabsContent>
        </Tabs>

        <Modal size="xl" isOpen={wechatPayIsOpen} onOpenChange={wechatPayOnOpenChange} data-sentry-element="Modal" data-sentry-source-file="PaymentV2.tsx">
          <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="PaymentV2.tsx">
            <ModalHeader className="flex justify-center" data-sentry-element="ModalHeader" data-sentry-source-file="PaymentV2.tsx">
              {paymentMethod === 1 ? "微信扫码支付" : "支付宝扫码支付"}
            </ModalHeader>
            <ModalBody data-sentry-element="ModalBody" data-sentry-source-file="PaymentV2.tsx">
              {isMutating ? <div>loading...</div> : <div className="flex flex-col items-center justify-center gap-4">
                  <span className="text-sm">
                    请在<span className="text-[#5D56FB]">15:00</span>
                    分钟内扫码支付当前订单，您可以随时在
                    <Link className="text-[#5D56FB]" href="/zh/tiktok/account">
                      【账户中心-订单】里查看
                    </Link>
                  </span>
                  {tradeInfo?.code_url && <div className="mb-4  size-[50%] bg-gray-200 p-4">
                      <QRCode size={128} style={{
                  height: "auto",
                  width: "100%",
                  margin: "auto"
                }} value={tradeInfo?.code_url} viewBox={`0 0 128 128`} />
                    </div>}
                </div>}
            </ModalBody>
          </ModalContent>
        </Modal>
        <AlertDialog open={alertDialogIsOpen} data-sentry-element="AlertDialog" data-sentry-source-file="PaymentV2.tsx">
          <AlertDialogContent data-sentry-element="AlertDialogContent" data-sentry-source-file="PaymentV2.tsx">
            <AlertDialogHeader data-sentry-element="AlertDialogHeader" data-sentry-source-file="PaymentV2.tsx">
              <AlertDialogTitle data-sentry-element="AlertDialogTitle" data-sentry-source-file="PaymentV2.tsx">您有一张待支付订单，请选择:</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter data-sentry-element="AlertDialogFooter" data-sentry-source-file="PaymentV2.tsx">
              <AlertDialogCancel onClick={() => setAlertDialogIsOpen(false)} data-sentry-element="AlertDialogCancel" data-sentry-source-file="PaymentV2.tsx">
                关闭
              </AlertDialogCancel>
              <AlertDialogAction asChild data-sentry-element="AlertDialogAction" data-sentry-source-file="PaymentV2.tsx">
                <Link href="/zh/tiktok/account" data-sentry-element="Link" data-sentry-source-file="PaymentV2.tsx">查看订单</Link>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Fragment>;
}
function InterestsItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-2" data-sentry-component="InterestsItem" data-sentry-source-file="PaymentV2.tsx">
      <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="PaymentV2.tsx">
        <path fillRule="evenodd" clipRule="evenodd" d="M9.76191 4.1838C10.0517 4.45396 10.081 4.92325 9.8275 5.23199L6.2577 9.49265C5.68583 10.189 4.66208 10.165 4.11957 9.44242L2.1528 7.09583C1.91227 6.77547 1.96101 6.30802 2.26167 6.05174C2.56233 5.79545 3.00105 5.84739 3.24158 6.16775L4.99768 8.5068L8.77816 4.25368C9.0317 3.94494 9.47215 3.91365 9.76191 4.1838Z" fill="#818B96" fillOpacity="0.6" data-sentry-element="path" data-sentry-source-file="PaymentV2.tsx" />
      </svg>

      <span className="text-pretty text-sm text-[rgba(8,10,46,0.84)]">
        {children}
      </span>
    </div>;
}
function priceFormatter(price?: number) {
  if (!price) return "";
  return `¥${Math.floor(price / 100)}`;
}
function QrCode() {
  return <div className="relative h-[187px] w-[326px] overflow-hidden rounded-md bg-[#F8F8FB]" data-sentry-component="QrCode" data-sentry-source-file="PaymentV2.tsx">
      <Image src="/pricing/pay_mentor.webp" style={{
      height: 176,
      width: 326
    }} alt="mentor_background" width="326" height="176" data-sentry-element="Image" data-sentry-source-file="PaymentV2.tsx" />
      <Image src="/mentor_qrcode.webp" className="absolute bottom-3 right-6" style={{
      height: 98,
      width: 98
    }} alt="qrcode" width="194" height="194" data-sentry-element="Image" data-sentry-source-file="PaymentV2.tsx" />
    </div>;
}